import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ExternalLink } from 'lucide-react';
import type { SICReportHistoryItem, SICReportRunStatus } from '@/types/sic-report.types';

interface Props {
    items: SICReportHistoryItem[];
    isLoading: boolean;
    onOpenDetail?: (reportId: string, runDate: string) => void;
}

const STATUS_VARIANT: Record<SICReportRunStatus, { label: string; color: string }> = {
    RUNNING: { label: 'En curso', color: 'bg-blue-500/20 text-blue-400' },
    COMPLETED: { label: 'OK', color: 'bg-emerald-500/20 text-emerald-400' },
    COMPLETED_WITH_ERRORS: { label: 'Con errores', color: 'bg-amber-500/20 text-amber-400' },
    FAILED: { label: 'Fallo', color: 'bg-red-500/20 text-red-400' },
};

function formatDate(iso: string): string {
    return new Date(iso + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SICHistoryTable({ items, isLoading, onOpenDetail }: Props) {
    if (isLoading) {
        return (
            <Card title="Historial" description="Corridas pasadas (auditoría)">
                <Skeleton className="h-32" />
            </Card>
        );
    }

    if (items.length === 0) {
        return (
            <Card title="Historial" description="Corridas pasadas (auditoría)">
                <p className="text-sm text-slate-400">Sin corridas registradas.</p>
            </Card>
        );
    }

    return (
        <Card title="Historial" description="Corridas pasadas (auditoría)">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
                            <th className="pb-2 text-left font-normal">Fecha</th>
                            <th className="pb-2 text-left font-normal">Estado</th>
                            <th className="pb-2 text-right font-normal">Eleg.</th>
                            <th className="pb-2 text-right font-normal">Conf.</th>
                            <th className="pb-2 text-right font-normal">Rech.</th>
                            <th className="pb-2 text-right font-normal">Err.</th>
                            <th className="pb-2 text-right font-normal">Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => {
                            const v = STATUS_VARIANT[item.status];
                            return (
                                <tr key={item.report_id} className="border-b border-slate-800/50">
                                    <td className="py-2 text-slate-200">{formatDate(item.run_date)}</td>
                                    <td className="py-2"><Badge className={v.color}>{v.label}</Badge></td>
                                    <td className="py-2 text-right text-slate-300">{item.eligible_count}</td>
                                    <td className="py-2 text-right text-emerald-400">{item.confirmed_count}</td>
                                    <td className="py-2 text-right text-amber-400">{item.rejected_count}</td>
                                    <td className="py-2 text-right text-red-400">{item.error_count}</td>
                                    <td className="py-2 text-right">
                                        {onOpenDetail && (
                                            <button
                                                onClick={() => onOpenDetail(item.report_id, item.run_date)}
                                                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                            >
                                                Detalle <ExternalLink size={12} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}
